import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SeagullBlack from '../images/04-black.webp';
import SeagullWhite from '../images/03-white.webp';
import SeagullGreen from '../images/byd-green.png';

function WarnaSeagull() {
  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <Helmet>
        <title>
          Pilihan Warna BYD Atto1 2025 - Mana yang Cocok untuk Kamu?
        </title>
        <meta
          name='description'
          content='Lihat pilihan warna BYD Atto1 2025 di BYD BSD Serpong: Black, White, dan Sprout Green. Pilih warna favoritmu dan temukan kepribadian mobil listrik impianmu!'
        />
        <meta
          name='keywords'
          content='Warna BYD Atto1, Warna Mobil BYD Atto1, Atto1 Sprout Green, Atto1 putih, Atto1 hitam, BYD BSD Serpong'
        />
        <link
          rel='canonical'
          href='https://bydbsdserpong.com/blog/pilihan-warna-byd-atto1'
        />
      </Helmet>
      <br />
      <br />
      <br />
      <br />
      <Typography variant='h3' component='h1' fontWeight='bold' gutterBottom>
        Pilihan Warna BYD Atto1 2025: Mana yang Cocok untuk Kamu?
      </Typography>

      <Typography
        variant='body1'
        paragraph
        sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
      >
        BYD Atto1 2025 bukan hanya menawarkan harga terjangkau dan teknologi
        canggih, tetapi juga pilihan warna yang bisa mencerminkan kepribadian
        kamu! ✨ Tersedia dalam beberapa warna utama seperti{' '}
        <strong>Black, White, dan Sprout Green</strong>. Setiap varian punya kesan
        tersendiri yang bisa bikin kamu jatuh cinta sejak pandangan pertama.
        Artikel ini akan mengupas tuntas makna, kelebihan visual, dan
        rekomendasi berdasarkan gaya hidup untuk tiap warna Atto1.
      </Typography>

      <Grid container spacing={4} sx={{ mt: 4 }}>
        {[
          {
            title: '🖤 BYD Atto1 Black',
            image: SeagullBlack,
            desc: `Warna hitam selalu identik dengan kemewahan, kekuatan, dan kesan profesional. BYD Atto1 dalam warna black cocok buat kamu yang ingin tampil elegan dan misterius di jalanan kota.`,
          },
          {
            title: '🤍 BYD Atto1 White',
            image: SeagullWhite,
            desc: `Putih adalah simbol dari kesederhanaan dan kebersihan. Dengan warna ini, BYD Atto1 terlihat seperti mobil masa depan yang sleek dan elegan.`,
          },
          {
            title: '🌿 BYD Atto1 Sprout Green',
            image: SeagullGreen,
            desc: `Warna Sprout Green memberi kesan segar, modern, dan berbeda tanpa terlihat berlebihan. Cocok untuk kamu yang ingin Atto1 tetap stand out di jalan kota.`,
          },
        ].map((color, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper
              elevation={3}
              sx={{ padding: 3, textAlign: 'center', borderRadius: 3 }}
            >
              <Box
                component='img'
                src={color.image}
                alt={color.title}
                sx={{
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'contain',
                  mb: 2,
                }}
              />
              <Typography variant='h5' fontWeight='bold' gutterBottom>
                {color.title}
              </Typography>
              <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                {color.desc}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Kenapa Warna Penting dalam Memilih Mobil?
        </Typography>
        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          Menurut berbagai studi, warna mobil dapat mencerminkan kepribadian dan
          mempengaruhi mood saat berkendara. Pemilik mobil hitam dianggap lebih
          berkelas, sementara pemilik mobil putih lebih perfeksionis dan
          praktis. Warna Sprout Green menunjukkan karakter segar dan modern, sementara
          warna hitam memberi kesan elegan dan tegas.
        </Typography>
        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          Selain itu, warna juga bisa berpengaruh pada nilai jual kembali dan
          visibilitas di jalan. Misalnya, warna putih lebih mudah terlihat di
          malam hari, sementara warna hitam sering terlihat lebih cepat kotor
          namun tetap jadi favorit karena tampilannya yang maskulin dan
          eksklusif.
        </Typography>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Tips Memilih Warna Atto1 Berdasarkan Kepribadian
        </Typography>
        <ul style={{ fontSize: '1.1rem', lineHeight: 2 }}>
          <li>
            <strong>Ekstrovert & Ceria:</strong> Pilih <strong>Sprout Green</strong>{' '}
            untuk tampil menonjol tanpa terlalu mencolok
          </li>
          <li>
            <strong>Minimalis & Rapi:</strong> Warna <strong>Putih</strong>{' '}
            cocok banget untuk kamu yang suka hal simpel dan bersih
          </li>
          <li>
            <strong>Stylish & Profesional:</strong> Warna <strong>Hitam</strong>{' '}
            cocok buat kamu yang ingin tampil elegan dan misterius
          </li>
        </ul>
      </Box>

      {/* Komentar Pelanggan Fiktif */}
      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          🗣️ Komentar Pelanggan Kami
        </Typography>
        <Grid container spacing={3} mt={2}>
          {[
            '“Warna Sprout Green-nya segar dan beda. Mobilnya kecil, tapi tetap terlihat modern.” — Rani, 24 tahun, Jakarta',
            '“Saya pilih warna putih karena terlihat futuristik dan bersih banget. Cocok banget buat tampil profesional.” — Andi, 29 tahun, Tangerang',
            '“Awalnya ragu sama warna hitam, tapi setelah lihat langsung, elegan banget. Cocok buat daily use ke kantor.” — Siska, 33 tahun, Depok',
          ].map((comment, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Paper
                elevation={1}
                sx={{ p: 3, borderLeft: '4px solid #1976d2' }}
              >
                <Typography variant='body1' sx={{ fontStyle: 'italic' }}>
                  {comment}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          ❓ Pertanyaan yang Sering Ditanyakan (FAQ)
        </Typography>
        <Box sx={{ mt: 3 }}>
          {[
            {
              q: 'Apakah warna mempengaruhi harga mobil?',
              a: 'Tidak, seluruh varian warna BYD Atto1 saat ini memiliki harga yang sama. Namun, tergantung permintaan dan tren pasar, warna tertentu bisa jadi lebih langka dan lebih cepat habis.',
            },
            {
              q: 'Apa warna favorit Gen Z untuk mobil listrik?',
              a: 'Menurut survei internal kami, warna Sprout Green dan White sering jadi pilihan Gen Z karena terlihat fresh, berbeda, dan mudah dipadukan dengan gaya harian.',
            },
            {
              q: 'Warna apa yang paling mudah dirawat?',
              a: 'Warna putih cenderung mudah dirawat karena tidak terlalu terlihat saat kotor ringan, berbeda dengan warna hitam yang sering menampilkan goresan kecil dan debu.',
            },
          ].map((item, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography variant='h6' fontWeight='bold'>
                {item.q}
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontSize: '1.05rem', lineHeight: 1.8 }}
              >
                {item.a}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* CTA section */}
      <Paper
        sx={{
          p: 4,
          mt: 6,
          backgroundColor: '#e8f5e9',
          borderLeft: '6px solid #43a047',
          borderRadius: 2,
        }}
      >
        <Typography variant='h5' fontWeight='bold' gutterBottom>
          🎨 Sudah Tentukan Warna Favoritmu?
        </Typography>
        <Typography variant='body1' gutterBottom>
          Hubungi kami sekarang dan pilih warna BYD Atto1 yang paling cocok
          dengan karaktermu!
        </Typography>
        <Typography variant='body2' sx={{ mt: 1 }}>
          📞{' '}
          <a
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20BYD%20Seagull%20warna'
            target='_blank'
            rel='noopener noreferrer'
          >
            Chat WhatsApp
          </a>{' '}
          atau follow kami di{' '}
          <a
            href='https://instagram.com/bydbsdserpong'
            target='_blank'
            rel='noopener noreferrer'
          >
            @bydbsdserpong
          </a>{' '}
          untuk info lebih lanjut dan update terbaru!
        </Typography>
      </Paper>
    </Box>
  );
}

export default WarnaSeagull;
